/**
 * Preview Report Mapper
 *
 * Transforms BatchValidationResult.invalidRows into the generic ErrorReportRow
 * shape consumed by generateErrorReportCSV. This mapper is specific to the
 * preview-upload step; a future import-report-mapper will handle server-side
 * rejection errors.
 *
 * This module is pure, synchronous, and side-effect-free.
 */

import type {
  BatchValidationResult,
  RowValidationResult,
} from '../validators/row-validator';
import type { NormalizedTemplate } from '../types/template';
import type { ErrorReportRow, ErrorReportColumn } from './error-report';

export interface PreviewReportResult {
  rows: ErrorReportRow[];
  columns: ErrorReportColumn[];
}

/**
 * Maps BatchValidationResult.invalidRows into the generic ErrorReportRow shape.
 *
 * For duplicate rows: errorCode = "duplicate", errorMessage = "Duplicate of row {N}"
 * For cell-error rows: errorCode/errorMessage = " AND "-delimited list of per-cell errors
 *   prefixed by column key (errorCode) or column label (errorMessage),
 *   in template column definition order.
 *
 * Only cells with valid === false are included in error strings.
 *
 * @param validationResult - The batch validation output from ParserController
 * @param template - The normalized template (provides column order and labels)
 * @returns Generic rows + columns for the CSV serializer
 */
export function mapPreviewReport(
  validationResult: BatchValidationResult,
  template: NormalizedTemplate,
): PreviewReportResult {
  const columns: ErrorReportColumn[] = template.columns.map((col) => ({
    key: col.key,
    label: col.label,
  }));

  const rows: ErrorReportRow[] = validationResult.invalidRows.map((row) =>
    mapRow(row, template),
  );

  return { rows, columns };
}

function mapRow(
  row: RowValidationResult,
  template: NormalizedTemplate,
): ErrorReportRow {
  if (row.duplicate) {
    return {
      rowIndex: row.rowIndex,
      data: row.data,
      errorCode: 'duplicate',
      errorMessage: `Duplicate of row ${row.duplicateOfRow}`,
    };
  }

  // Cell-error row: collect invalid cells in template column definition order
  const codes: string[] = [];
  const messages: string[] = [];

  for (const col of template.columns) {
    const cell = row.cells[col.key];
    if (cell && !cell.valid) {
      codes.push(`${col.key}: ${cell.errorCode}`);
      messages.push(`${col.label}: ${cell.error ?? ''}`);
    }
  }

  return {
    rowIndex: row.rowIndex,
    data: row.data,
    errorCode: codes.join(' AND '),
    errorMessage: messages.join(' AND '),
  };
}
